import React, { useEffect, useMemo, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronRight, Check, ShieldCheck, CalendarClock } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { makeAuthenticatedJsonRequest } from '../utils/api'
import { trackEvent } from '../utils/analytics'

/**
 * Full-screen questionnaire that gates the marketing site for first-time,
 * logged-out visitors. The site stays mounted behind a blur; the overlay is
 * dismissed once the visitor answers (or skips via "just looking").
 *
 * It is never shown when:
 *  - rendering on the server (mount guard — keeps SSR/prerender HTML intact),
 *  - the visitor is logged in (useAuth),
 *  - this browser already completed/skipped it (localStorage flag).
 */

const DONE_KEY = 'zy:questionnaire-done'
const QUESTIONNAIRE_EMAIL_KEY = 'zy:questionnaire-email'
const QUESTIONNAIRE_PHONE_KEY = 'zy:questionnaire-phone'
const CALENDLY_URL = 'https://calendly.com/shabblezam/30min'
const FREE_OFFER_NAME = 'Zypheron Desktop trial'
const FREE_OFFER_DETAILS = [
  '14-day free trial with no credit card required',
  'Keep findings and evidence organized from day one',
  'Use the local-first desktop workflow before you choose a plan',
]

type AnswerKey = 'teamSize' | 'role' | 'yearsInCyber' | 'need' | 'annualSpend' | 'contact'

interface QuestionConfig {
  /** answer key stored + sent to the backend */
  key: AnswerKey
  title: string
  options: { value: string; label: string }[]
  /** when true, an "Other" option reveals a free-text box */
  allowOther?: boolean
  /** when true, an explicit "Skip" control is shown (optional question) */
  skippable?: boolean
}

// Labels live here so copy is easy to tweak. `value` is the canonical token
// stored server-side; it must match the Joi enums in backend/routes/user.ts.
const QUESTIONS: QuestionConfig[] = [
  {
    key: 'teamSize',
    title: 'How big is your team?',
    options: [
      { value: 'just_me', label: 'Just me' },
      { value: '3-5', label: '3–5 people' },
      { value: '5-10', label: '5–10 people' },
      { value: '15-25+', label: '15–25+ people' },
    ],
  },
  {
    key: 'role',
    title: "What's your role?",
    allowOther: true,
    options: [
      { value: 'pentester', label: 'Pentester' },
      { value: 'ciso', label: 'CISO' },
      { value: 'ceo', label: 'CEO' },
    ],
  },
  {
    key: 'yearsInCyber',
    title: 'How long have you been in cyber?',
    options: [
      { value: '0-6m', label: '0–6 months' },
      { value: '1-3y', label: '1–3 years' },
      { value: '3-5y', label: '3–5 years' },
      { value: '5-10y+', label: '5–10 years+' },
    ],
  },
  {
    key: 'need',
    title: 'What do you need?',
    allowOther: true,
    options: [
      { value: 'save_time', label: 'Save time on my engagements' },
      { value: 'save_money', label: 'Save money on operational costs' },
      { value: 'more_clients', label: 'Get more clients for my company' },
      { value: 'be_secure', label: 'Be more secure as a company' },
    ],
  },
  {
    key: 'annualSpend',
    title: 'Annual cybersecurity infrastructure spend?',
    skippable: true,
    options: [
      { value: '0-1k', label: '$0–1k' },
      { value: '3-5k', label: '$3–5k' },
      { value: '10-50k', label: '$10–50k' },
      { value: '100k+', label: '$100k+' },
    ],
  },
  {
    key: 'contact',
    title: 'Where should we send your next step?',
    options: [],
  },
]

type Phase = 'welcome' | 'questions' | 'unlocked' | 'demo' | 'closed'

interface Answers {
  teamSize?: string
  role?: string
  roleOther?: string
  yearsInCyber?: string
  need?: string
  needOther?: string
  annualSpend?: string
  email?: string
  phone?: string
}

const slide = {
  initial: { opacity: 0, x: 40 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -40 },
}

const QuestionnaireGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [mounted, setMounted] = useState(false)
  const [alreadyDone, setAlreadyDone] = useState(true) // assume done until checked (SSR-safe)

  const [phase, setPhase] = useState<Phase>('welcome')
  const [step, setStep] = useState(0)
  const [answers, setAnswers] = useState<Answers>({})

  // "Other" free-text handling (role + need)
  const [otherActive, setOtherActive] = useState(false)
  const [otherText, setOtherText] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [contactPhone, setContactPhone] = useState('')
  const [contactFieldErrors, setContactFieldErrors] = useState<{ email?: string; phone?: string }>({})

  // Welcome step — video is opt-in, never autoplays.
  const [welcomeVideoOpen, setWelcomeVideoOpen] = useState(false)

  // Demo step
  const [demoName, setDemoName] = useState('')
  const [demoLinkedIn, setDemoLinkedIn] = useState('')
  const [demoHoneypot, setDemoHoneypot] = useState('')
  const mountedAtRef = useRef(Date.now())
  const [demoSubmitting, setDemoSubmitting] = useState(false)
  const [demoSubmitted, setDemoSubmitted] = useState(false)
  const [demoError, setDemoError] = useState<string | null>(null)

  // Client mount + completion check (never runs on the server).
  useEffect(() => {
    setMounted(true)
    try {
      const shouldReset = new URLSearchParams(window.location.search).get('questionnaire') === 'reset'
      if (shouldReset) {
        localStorage.removeItem(DONE_KEY)
      }
      setAlreadyDone(Boolean(localStorage.getItem(DONE_KEY)))
    } catch {
      setAlreadyDone(false)
    }
  }, [])

  const isAdminRoute = location.pathname.startsWith('/admin')
  // Purchase/download intent is already explicit on these routes — gating them
  // with a lead-capture detour costs more conversion than it gains.
  const isIntentRoute = location.pathname.startsWith('/billing') || location.pathname.startsWith('/download') || location.pathname.startsWith('/qualify')
  const shouldGate = mounted && !loading && !user && !alreadyDone && phase !== 'closed' && !isAdminRoute && !isIntentRoute

  useEffect(() => {
    if (shouldGate) {
      trackEvent('questionnaire_view', { path: location.pathname })
    }
  }, [location.pathname, shouldGate])

  useEffect(() => {
    if (shouldGate && phase === 'demo') {
      trackEvent('demo_step_view', { hasEmail: Boolean(answers.email), hasPhone: Boolean(answers.phone) })
    }
  }, [answers.email, answers.phone, phase, shouldGate])

  // Lock body scroll while the overlay is visible.
  useEffect(() => {
    if (!shouldGate) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [shouldGate])

  const current = QUESTIONS[step]

  // Persist answers, mark done, run the unlock animation, then either show the
  // demo step (completed) or close (skipped).
  const finish = (finalAnswers: Answers, skipped: boolean) => {
    try {
      localStorage.setItem(DONE_KEY, new Date().toISOString())
      if (finalAnswers.email) localStorage.setItem(QUESTIONNAIRE_EMAIL_KEY, finalAnswers.email)
      if (finalAnswers.phone) localStorage.setItem(QUESTIONNAIRE_PHONE_KEY, finalAnswers.phone)
    } catch {
      /* private mode / disabled storage — gate just won't persist */
    }

    // Fire-and-forget: a failed log must never trap the visitor behind the gate.
    makeAuthenticatedJsonRequest('/user/questionnaire', {
      method: 'POST',
      body: {
        teamSize: finalAnswers.teamSize,
        role: finalAnswers.role,
        roleOther: finalAnswers.roleOther,
        yearsInCyber: finalAnswers.yearsInCyber,
        need: finalAnswers.need,
        needOther: finalAnswers.needOther,
        annualSpend: finalAnswers.annualSpend,
        email: finalAnswers.email,
        phone: finalAnswers.phone,
        skipped,
      },
    }).catch(() => {})

    trackEvent(skipped ? 'questionnaire_skip' : 'questionnaire_complete', {
      hasEmail: Boolean(finalAnswers.email),
      hasPhone: Boolean(finalAnswers.phone),
    })

    if (skipped) {
      // No celebratory animation for an opt-out — just release the visitor.
      setPhase('closed')
      return
    }
    setPhase('unlocked')
    window.setTimeout(() => {
      setPhase('demo')
    }, 1400)
  }

  const goNext = (next: Answers) => {
    setAnswers(next)
    setOtherActive(false)
    setOtherText('')
    if (step < QUESTIONS.length - 1) {
      setStep(step + 1)
    } else {
      finish(next, false)
    }
  }

  const selectOption = (value: string) => {
    goNext({ ...answers, [current.key]: value })
  }

  const submitContact = (e: React.FormEvent) => {
    e.preventDefault()
    const email = contactEmail.trim()
    const phone = contactPhone.trim()
    const fieldErrors: { email?: string; phone?: string } = {}
    if (!email) {
      fieldErrors.email = 'Enter an email address.'
    }
    if (fieldErrors.email || fieldErrors.phone) {
      setContactFieldErrors(fieldErrors)
      return
    }

    const next = { ...answers, email, phone }
    goNext(next)
  }

  const submitOther = () => {
    const trimmed = otherText.trim()
    if (!trimmed) return
    const next: Answers = { ...answers, [current.key]: 'other' }
    if (current.key === 'role') next.roleOther = trimmed
    if (current.key === 'need') next.needOther = trimmed
    goNext(next)
  }

  // Optional-question skip (annual spend): advances without recording an answer.
  const skipQuestion = () => goNext({ ...answers })

  // "Just looking" — bail out of the whole flow, saving whatever's answered,
  // and always land on the homepage rather than wherever they currently are.
  const justLooking = () => {
    finish(answers, true)
    navigate('/')
  }

  const continueTo = (path: '/billing/ide' | '/download') => {
    try {
      localStorage.setItem(DONE_KEY, new Date().toISOString())
    } catch {
      /* private mode / disabled storage — gate just won't persist */
    }
    trackEvent('questionnaire_get_it_now', { path: location.pathname, destination: path })
    setPhase('closed')
    navigate(path)
  }

  const submitDemo = async (e: React.FormEvent) => {
    e.preventDefault()
    setDemoError(null)
    const fullName = demoName.trim()
    const email = answers.email?.trim() || ''
    const phone = answers.phone?.trim() || ''
    const linkedin = demoLinkedIn.trim()
    if (!email) {
      setDemoError('Finish the email step first, then request setup help.')
      return
    }
    // Honeypot ("company") + minimum fill-time trap. Bots that auto-fill every
    // field or submit instantly get a fake success with nothing sent — no
    // error text that would help them adapt.
    const elapsedMs = Date.now() - mountedAtRef.current
    if (demoHoneypot.trim() || elapsedMs < 1500) {
      setDemoSubmitted(true)
      return
    }
    setDemoSubmitting(true)
    try {
      await makeAuthenticatedJsonRequest('/user/waitlist', {
        method: 'POST',
        body: {
          email,
          fullName: fullName || undefined,
          source: 'demo_request',
          interestLevel: 'high',
          metadata: {
            phone,
            linkedin: linkedin || undefined,
            freeOffer: FREE_OFFER_NAME,
            answers: {
              teamSize: answers.teamSize,
              role: answers.role,
              roleOther: answers.roleOther,
              yearsInCyber: answers.yearsInCyber,
              need: answers.need,
              needOther: answers.needOther,
              annualSpend: answers.annualSpend,
              email: answers.email,
              phone: answers.phone,
            },
            page: typeof window !== 'undefined' ? window.location.pathname : undefined,
          },
        },
      })
      setDemoSubmitted(true)
      trackEvent('demo_help_submit_success', { hasPhone: Boolean(phone) })
    } catch (err: any) {
      setDemoError(err?.message || 'Could not submit. Try again in a moment.')
      trackEvent('demo_help_submit_error', { hasPhone: Boolean(phone) })
    } finally {
      setDemoSubmitting(false)
    }
  }

  const progress = useMemo(() => ((step + 1) / QUESTIONS.length) * 100, [step])

  return (
    <>
      {children}
      <AnimatePresence>
        {shouldGate && (
          <motion.div
            key="gate"
            initial={{ opacity: 0, backdropFilter: 'blur(0px)', WebkitBackdropFilter: 'blur(0px)' }}
            animate={{ opacity: 1, backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)' }}
            exit={{
              opacity: 0,
              backdropFilter: 'blur(0px)',
              WebkitBackdropFilter: 'blur(0px)',
              transition: { duration: 0.55, ease: 'easeInOut' },
            }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4"
            role="dialog"
            aria-modal="true"
            aria-label="Visitor questionnaire"
          >
            <motion.div
              initial={{ scale: 0.96, y: 12, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              transition={{ duration: 0.3 }}
              // Close choreography: card lifts up, scales past 1, and dissolves into
              // blur while the backdrop clears — the site "snaps into focus" behind it.
              exit={{
                scale: 1.12,
                y: -56,
                rotate: 1.5,
                opacity: 0,
                filter: 'blur(6px)',
                transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
              }}
              className="relative w-full max-w-lg border border-white/10 bg-panel/80 rounded-2xl shadow-2xl overflow-hidden"
            >
              {/* Expanding green pulse-ring that fires on close (exit only) */}
              <motion.div
                aria-hidden
                className="pointer-events-none absolute inset-0 rounded-2xl border-2 border-zypheron-500"
                initial={{ opacity: 0, scale: 1 }}
                exit={{ opacity: [0, 0.8, 0], scale: 1.6, transition: { duration: 0.5, ease: 'easeOut' } }}
              />
              {/* WELCOME (compact — video is opt-in, no full-screen takeover) */}
              {phase === 'welcome' && (
                <div className="p-7 sm:p-9">
                  <div className="mb-4 inline-flex items-center gap-2 border border-zypheron-500/25 bg-zypheron-500/10 px-3 py-1.5 text-[11px] font-mono-tech uppercase tracking-[0.22em] text-zypheron-400">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    Quick setup
                  </div>
                  <h2 className="text-2xl font-bold text-white font-display">Welcome to Zypheron</h2>
                  <p className="mt-3 text-sm leading-6 text-gray-400">
                    Answer 5 quick questions (30 seconds) so we can point you at the right workflow, or skip straight in.
                  </p>

                  {welcomeVideoOpen ? (
                    <div className="mt-5 tech-panel p-2 bg-white/[0.02] border-zypheron-500/20">
                      <video
                        src="/videos/walkthrough.mp4"
                        className="w-full rounded-[4px] block"
                        controls
                        autoPlay
                        muted
                        playsInline
                        preload="metadata"
                      />
                    </div>
                  ) : (
                    <button
                      onClick={() => setWelcomeVideoOpen(true)}
                      className="mt-5 flex w-full items-center justify-center gap-2 border border-white/10 px-4 py-3 text-xs font-mono-tech uppercase tracking-[0.2em] text-gray-300 transition hover:border-white/25 hover:bg-white/5 rounded-lg"
                    >
                      ▶ Watch 60-second walkthrough
                    </button>
                  )}

                  <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                    <button
                      onClick={() => continueTo('/billing/ide')}
                      className="flex flex-1 items-center justify-center gap-2 bg-zypheron-500 px-5 py-3 text-sm font-bold uppercase tracking-[0.18em] text-black transition hover:bg-zypheron-400 rounded-lg"
                    >
                      <span>Start 14-day trial</span>
                      <ChevronRight className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => {
                        trackEvent('questionnaire_start', { path: location.pathname })
                        setPhase('questions')
                      }}
                      className="flex flex-1 items-center justify-center border border-white/10 px-5 py-3 text-sm font-bold uppercase tracking-[0.18em] text-gray-300 transition hover:border-white/25 hover:bg-white/5 rounded-lg"
                    >
                      Answer questionnaire
                    </button>
                  </div>
                  <div className="mt-4 flex gap-5 text-xs font-mono-tech uppercase tracking-[0.2em]">
                    <button onClick={() => continueTo('/download')} className="text-gray-500 transition-colors hover:text-gray-300">
                      Download Desktop
                    </button>
                    <button onClick={justLooking} className="text-gray-500 transition-colors hover:text-gray-300">
                      Just looking →
                    </button>
                  </div>
                </div>
              )}

              {/* QUESTIONS */}
              {phase === 'questions' && (
                <div className="p-7 sm:p-9">
                  {/* progress */}
                  <div className="mb-6 flex items-center gap-3">
                    <div className="flex-1 h-1 rounded-full bg-white/10 overflow-hidden">
                      <motion.div
                        className="h-full bg-zypheron-500"
                        initial={false}
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 0.35 }}
                      />
                    </div>
                    <span className="text-[11px] font-mono-tech uppercase tracking-[0.24em] text-zypheron-400">
                      {step + 1}/{QUESTIONS.length}
                    </span>
                  </div>

                  <AnimatePresence mode="wait">
                    <motion.div
                      key={current.key}
                      variants={slide}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                      transition={{ duration: 0.28 }}
                    >
                      <h2 className="text-2xl font-bold text-white font-display">{current.title}</h2>

                      {current.key === 'contact' ? (
                        <form onSubmit={submitContact} className="mt-6 grid gap-3">
                          <p className="text-sm leading-6 text-gray-400">
                            Enter your email so we can attach your answers to the right lead record. Phone is optional for higher-intent follow-up.
                          </p>
                          <div>
                            <input
                              autoFocus
                              aria-label="Email address"
                              type="email"
                              value={contactEmail}
                              onChange={(e) => {
                                setContactEmail(e.target.value)
                                if (contactFieldErrors.email) {
                                  setContactFieldErrors((current) => ({ ...current, email: undefined }))
                                }
                              }}
                              placeholder="operator@company.com"
                              autoComplete="email"
                              inputMode="email"
                              aria-invalid={Boolean(contactFieldErrors.email)}
                              aria-describedby={contactFieldErrors.email ? 'questionnaire-contact-email-error' : undefined}
                              className="w-full border border-white/10 bg-[#07090a] px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-gray-600 focus:border-zypheron-500 rounded-lg"
                            />
                            {contactFieldErrors.email && (
                              <p id="questionnaire-contact-email-error" className="mt-1.5 text-xs text-red-300">
                                {contactFieldErrors.email}
                              </p>
                            )}
                          </div>
                          <div>
                            <input
                              aria-label="Phone number"
                              type="tel"
                              value={contactPhone}
                              onChange={(e) => {
                                setContactPhone(e.target.value)
                                if (contactFieldErrors.phone) {
                                  setContactFieldErrors((current) => ({ ...current, phone: undefined }))
                                }
                              }}
                              placeholder="Phone number"
                              autoComplete="tel"
                              inputMode="tel"
                              aria-invalid={Boolean(contactFieldErrors.phone)}
                              aria-describedby={contactFieldErrors.phone ? 'questionnaire-contact-phone-error' : undefined}
                              className="w-full border border-white/10 bg-[#07090a] px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-gray-600 focus:border-zypheron-500 rounded-lg"
                            />
                            {contactFieldErrors.phone && (
                              <p id="questionnaire-contact-phone-error" className="mt-1.5 text-xs text-red-300">
                                {contactFieldErrors.phone}
                              </p>
                            )}
                          </div>
                          <button
                            type="submit"
                            className="flex items-center justify-center gap-2 bg-zypheron-500 px-5 py-3 text-sm font-bold uppercase tracking-[0.18em] text-black transition hover:bg-zypheron-400 rounded-lg"
                          >
                            <span>Submit answers</span>
                            <ChevronRight className="h-4 w-4" />
                          </button>
                        </form>
                      ) : (
                        <div className="mt-6 grid gap-3">
                          {current.options.map((opt) => (
                            <button
                              key={opt.value}
                              onClick={() => selectOption(opt.value)}
                              className="group flex items-center justify-between border border-white/10 bg-[#07090a] px-4 py-3.5 text-left text-sm text-white transition-colors hover:border-zypheron-500 hover:bg-zypheron-500/10 rounded-lg"
                            >
                              <span>{opt.label}</span>
                              <ChevronRight className="h-4 w-4 text-gray-600 transition-colors group-hover:text-zypheron-400" />
                            </button>
                          ))}

                          {current.allowOther && !otherActive && (
                            <button
                              onClick={() => setOtherActive(true)}
                              className="group flex items-center justify-between border border-white/10 bg-[#07090a] px-4 py-3.5 text-left text-sm text-white transition-colors hover:border-zypheron-500 hover:bg-zypheron-500/10 rounded-lg"
                            >
                              <span>Other…</span>
                              <ChevronRight className="h-4 w-4 text-gray-600 transition-colors group-hover:text-zypheron-400" />
                            </button>
                          )}

                          {current.allowOther && otherActive && (
                            <div className="flex flex-col sm:flex-row gap-2">
                              <input
                                autoFocus
                                value={otherText}
                                onChange={(e) => setOtherText(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && submitOther()}
                                aria-label={current.key === 'role' ? 'Your role' : 'Other response'}
                                placeholder={current.key === 'role' ? 'Your role' : 'What do you need?'}
                                className="flex-1 border border-white/10 bg-[#07090a] px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-gray-600 focus:border-zypheron-500 rounded-lg"
                              />
                              <button
                                onClick={submitOther}
                                className="flex items-center justify-center gap-2 bg-zypheron-500 px-5 py-3 text-sm font-bold uppercase tracking-[0.18em] text-black transition hover:bg-zypheron-400 rounded-lg"
                              >
                                Next
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="mt-6 flex items-center justify-between">
                        {current.skippable ? (
                          <button
                            onClick={skipQuestion}
                            className="text-xs font-mono-tech uppercase tracking-[0.2em] text-gray-500 transition-colors hover:text-gray-300"
                          >
                            Skip this →
                          </button>
                        ) : (
                          <span />
                        )}
                        <button
                          onClick={justLooking}
                          className="text-xs font-mono-tech uppercase tracking-[0.2em] text-gray-600 transition-colors hover:text-gray-400"
                        >
                          Just looking →
                        </button>
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </div>
              )}

              {/* UNLOCK ANIMATION */}
              {phase === 'unlocked' && (
                <div className="p-12 flex flex-col items-center justify-center text-center">
                  <motion.div
                    initial={{ scale: 0, rotate: -20 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: 'spring', stiffness: 260, damping: 18 }}
                    className="flex h-20 w-20 items-center justify-center rounded-full bg-zypheron-500/15 border border-zypheron-500/40"
                  >
                    <Check className="h-10 w-10 text-zypheron-400" />
                  </motion.div>
                  <motion.h2
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                    className="mt-6 text-2xl font-bold text-white font-display"
                  >
                    Unlocked
                  </motion.h2>
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="mt-2 text-sm text-gray-400"
                  >
                    Thanks. The site is all yours.
                  </motion.p>
                </div>
              )}

              {/* DEMO CAPTURE (optional) */}
              {phase === 'demo' && (
                <div className="p-7 sm:p-9">
                  <div className="mb-3 flex items-center gap-3 text-[11px] font-mono-tech uppercase tracking-[0.24em] text-zypheron-400">
                    <ShieldCheck className="h-4 w-4" />
                    Optional
                  </div>

                  {demoSubmitted ? (
                    <>
                      <h2 className="text-2xl font-bold text-white font-display">You're set</h2>
                      <p className="mt-2 text-sm text-gray-400">
                        Download Zypheron Desktop and sign in to start the 14-day no-card trial. Setup help stays optional.
                      </p>
                      <a
                        href="/download"
                        className="mt-5 inline-flex items-center justify-center gap-2 bg-zypheron-500 px-5 py-3 text-sm font-bold uppercase tracking-[0.18em] text-black transition hover:bg-zypheron-400 rounded-lg"
                      >
                        Download Desktop
                        <ChevronRight className="h-4 w-4" />
                      </a>
                      <a
                        href="/billing"
                        onClick={() => trackEvent('questionnaire_skip_to_checkout', { surface: 'demo_success' })}
                        className="mt-4 block text-xs font-mono-tech uppercase tracking-[0.2em] text-zypheron-400 transition-colors hover:text-zypheron-300"
                      >
                        Skip to checkout →
                      </a>
                      <button
                        onClick={() => setPhase('closed')}
                        className="mt-4 block text-xs font-mono-tech uppercase tracking-[0.2em] text-gray-500 transition-colors hover:text-gray-300"
                      >
                        Continue to site →
                      </button>
                      <a
                        href={CALENDLY_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackEvent('calendly_click', { surface: 'questionnaire_success' })}
                        className="mt-4 inline-flex items-center justify-center gap-2 text-xs font-mono-tech uppercase tracking-[0.2em] text-gray-400 transition hover:text-white"
                      >
                        <CalendarClock className="h-4 w-4" />
                        Book setup help
                      </a>
                    </>
                  ) : (
                    <>
                      <h2 className="text-2xl font-bold text-white font-display">Download Zypheron Desktop</h2>
                      <p className="mt-2 text-sm text-gray-400">
                        Start the local desktop workflow now. Sign in after install to get the 14-day no-card trial, organize evidence, and move toward cleaner reporting without committing to a call first.
                      </p>
                      <div className="mt-4 border border-zypheron-500/20 bg-zypheron-500/10 p-4 rounded-lg">
                        <div className="text-[11px] font-mono-tech uppercase tracking-[0.22em] text-zypheron-300">
                          Primary next step
                        </div>
                        <h3 className="mt-2 text-base font-semibold text-white">{FREE_OFFER_NAME}</h3>
                        <ul className="mt-3 grid gap-2 text-sm text-gray-300">
                          {FREE_OFFER_DETAILS.map((item) => (
                            <li key={item} className="flex gap-2">
                              <Check className="mt-0.5 h-4 w-4 shrink-0 text-zypheron-400" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <a
                        href="/download"
                        className="mt-5 flex items-center justify-center gap-2 bg-zypheron-500 px-5 py-3 text-sm font-bold uppercase tracking-[0.18em] text-black transition hover:bg-zypheron-400 rounded-lg"
                      >
                        <span>Download Desktop</span>
                        <ChevronRight className="h-4 w-4" />
                      </a>
                      <div className="my-4 flex items-center gap-3 text-[10px] font-mono-tech uppercase tracking-[0.22em] text-gray-600">
                        <div className="h-px flex-1 bg-white/10" />
                        <span>optional help</span>
                        <div className="h-px flex-1 bg-white/10" />
                      </div>
                      <form onSubmit={submitDemo} className="grid gap-3">
                        <p className="text-sm text-gray-400">
                          Want setup help? We will use {answers.email || 'the email you entered'} for follow-up. Phone is optional.
                        </p>
                        <input
                          type="text"
                          name="company"
                          value={demoHoneypot}
                          onChange={(e) => setDemoHoneypot(e.target.value)}
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          className="absolute -left-[9999px] h-0 w-0 opacity-0"
                        />
                        <input
                          aria-label="Name"
                          type="text"
                          value={demoName}
                          onChange={(e) => setDemoName(e.target.value)}
                          placeholder="Name (optional)"
                          autoComplete="name"
                          className="w-full border border-white/10 bg-[#07090a] px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-gray-600 focus:border-zypheron-500 rounded-lg"
                        />
                        <input
                          aria-label="LinkedIn profile"
                          type="url"
                          value={demoLinkedIn}
                          onChange={(e) => setDemoLinkedIn(e.target.value)}
                          placeholder="LinkedIn URL (optional)"
                          className="w-full border border-white/10 bg-[#07090a] px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-gray-600 focus:border-zypheron-500 rounded-lg"
                        />
                        <button
                          type="submit"
                          disabled={demoSubmitting}
                          className="flex items-center justify-center gap-2 border border-white/10 px-5 py-3 text-sm font-bold uppercase tracking-[0.18em] text-gray-200 transition hover:border-white/25 hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-50 rounded-lg"
                        >
                          <span>{demoSubmitting ? 'Sending' : 'Request setup help'}</span>
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </form>
                      <a
                        href={CALENDLY_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackEvent('calendly_click', { surface: 'questionnaire_demo' })}
                        className="inline-flex items-center justify-center gap-2 border border-white/10 px-5 py-3 text-sm font-bold uppercase tracking-[0.18em] text-gray-200 transition hover:border-white/25 hover:bg-white/5 rounded-lg"
                      >
                        <CalendarClock className="h-4 w-4" />
                        Book setup help
                      </a>
                      {demoError && (
                        <div className="mt-3 border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-300 rounded-lg">
                          {demoError}
                        </div>
                      )}
                      <a
                        href="/billing"
                        onClick={() => trackEvent('questionnaire_skip_to_checkout', { surface: 'demo_offer' })}
                        className="mt-4 block text-xs font-mono-tech uppercase tracking-[0.2em] text-zypheron-400 transition-colors hover:text-zypheron-300"
                      >
                        Skip to checkout →
                      </a>
                      <button
                        onClick={() => setPhase('closed')}
                        className="mt-4 block text-xs font-mono-tech uppercase tracking-[0.2em] text-gray-500 transition-colors hover:text-gray-300"
                      >
                        Maybe later →
                      </button>
                    </>
                  )}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default QuestionnaireGate
